import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { apiClient } from '@/lib/api-client';
import { ROUTES } from '@/lib/routes';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { buildGeneratedCampaignName } from '../lib/campaign-name';
import { buildMailAssistantKnowledgePayload } from '../lib/mail-assistant';
import { useWizardStore } from '../lib/wizard-store';

function summarize(text: string): string {
  if (!text) return '-';
  const first = (text.split('\n')[0] ?? '').trim();
  return first.length > 80 ? `${first.slice(0, 80)}...` : first;
}

function splitFreeformList(text: string): string[] {
  return text
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function getErrorMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  );
}

function parseSuppressionBlock(
  text: string,
  scope: 'workspace' | 'brand' | 'campaign',
  brandScope: string,
) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
      const [prefix, ...rest] = line.split(':');
      const rawValue = rest.join(':').trim();

      if (rest.length === 0) {
        return [
          {
            scope,
            brandScope: scope === 'brand' ? brandScope || null : null,
            identityType: line.includes('@') ? 'email' : 'domain',
            identityValue: line,
            reason: 'manual',
          },
        ];
      }

      if (
        prefix !== 'email' &&
        prefix !== 'domain' &&
        prefix !== 'company_domain' &&
        prefix !== 'company_name'
      ) {
        return [];
      }

      if (!rawValue) return [];

      return [
        {
          scope,
          brandScope: scope === 'brand' ? brandScope || null : null,
          identityType: prefix,
          identityValue: rawValue,
          reason: 'manual',
        },
      ];
    });
}

function buildFitConfigPayload(
  campaignId: string,
  workspaceId: string,
  store: ReturnType<typeof useWizardStore.getState>,
) {
  const fitGate = store.fit_gate;
  const brandScope = fitGate.brand_scope.trim();

  return {
    workspace_id: workspaceId,
    campaign_id: campaignId,
    brand_scope: brandScope || undefined,
    policy: {
      positiveContext: {
        idealCustomerProfile: fitGate.ideal_customer_profile.trim(),
        preferredRoles: splitFreeformList(fitGate.preferred_roles),
        preferredSectors: splitFreeformList(fitGate.preferred_sectors),
        preferredCompanyTypes: splitFreeformList(fitGate.preferred_company_types),
      },
      negativeContext: {
        antiProfile: fitGate.anti_profile.trim(),
        badFitExamples: splitFreeformList(fitGate.bad_fit_examples),
      },
      hardRules: {
        blockedRoles: splitFreeformList(fitGate.blocked_roles),
        blockedOrganizationTypes: splitFreeformList(fitGate.blocked_organization_types),
        blockedSectors: splitFreeformList(fitGate.blocked_sectors),
        blockedCountries: splitFreeformList(fitGate.blocked_countries),
        blockedLanguages: splitFreeformList(fitGate.blocked_languages),
        blockedDomains: splitFreeformList(fitGate.blocked_domains),
        blockedEmails: splitFreeformList(fitGate.blocked_emails),
        blockedCompanies: splitFreeformList(fitGate.blocked_companies),
      },
      scoringRules: {},
      sendThreshold: fitGate.send_threshold,
      reviewThreshold: fitGate.review_threshold,
    },
    suppression_entries: [
      ...parseSuppressionBlock(fitGate.workspace_suppressions, 'workspace', brandScope),
      ...parseSuppressionBlock(fitGate.brand_suppressions, 'brand', brandScope),
      ...parseSuppressionBlock(fitGate.campaign_suppressions, 'campaign', brandScope),
    ],
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{title}</p>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}

export function W8_Summary() {
  const store = useWizardStore();
  const navigate = useNavigate();
  const { currentWorkspace } = useCurrentWorkspace();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generatedCampaignName = useMemo(
    () =>
      buildGeneratedCampaignName({
        targetCountry: store.target_country,
        language: store.language,
        leadscrapeIndustries: store.leadscrape_targeting.industries,
        preferredSectors: store.fit_gate.preferred_sectors,
        campaignIdentifier: store.campaign_identifier,
        offerTitle: store.offer_title,
        senderCompanyName: store.sender_company_name,
      }),
    [
      store.campaign_identifier,
      store.fit_gate.preferred_sectors,
      store.language,
      store.leadscrape_targeting.industries,
      store.offer_title,
      store.sender_company_name,
      store.target_country,
    ],
  );

  const selectedMailProviders =
    store.mail_providers.length > 0
      ? store.mail_providers
      : store.mail_provider
        ? [store.mail_provider]
        : [];
  const legacyMailProvider =
    selectedMailProviders.length === 1 ? selectedMailProviders[0] : undefined;
  const hasMail = store.channels_enabled.includes('mail');
  const hasFormfill = store.channels_enabled.includes('formfill');
  const hasCall = store.channels_enabled.includes('call');
  const hasLinkedInOutreach = store.channels_enabled.includes('linkedin_outreach');

  const fitConfigPayload = buildFitConfigPayload(
    'campaign-preview',
    currentWorkspace?.id ?? '',
    store,
  );
  const payload = {
    workspace_id: currentWorkspace?.id ?? '',
    name: store.campaign_name,
    channels_enabled: store.channels_enabled,
    mail_then_call_fallback: store.mail_then_call_fallback,
    formfill_runs_in_parallel: store.formfill_runs_in_parallel,
    abandon_if_nothing: store.abandon_if_nothing,
    sender_company_name: store.sender_company_name,
    sender_website: store.sender_website,
    sender_description: store.sender_description,
    sender_first_name: store.sender_first_name,
    sender_role: store.sender_role,
    sender_email: store.sender_email,
    offer_title: store.offer_title,
    offer_description: store.offer_description,
    offer_url: store.offer_url,
    target_country: store.target_country,
    target_city: store.target_city,
    target_region: store.target_region,
    language: store.language,
    email_templates: store.email_templates,
    mail_provider: legacyMailProvider,
    mail_provider_email_account_ids: store.mail_provider_email_account_ids,
    form_message_template: store.form_message_template,
    form_message_optimize_per_contact: store.form_message_optimize_per_contact,
    reply_to_address: store.reply_to_address,
    reply_to_brand_handler: store.reply_to_brand_handler,
    bigcall_workspace_id: store.bigcall_workspace_id,
    bigcall_campaign_id: store.bigcall_campaign_id,
    new_bigcall_campaign_name: store.new_bigcall_campaign_name,
    enrichment_providers: store.enrichment_providers,
    enrichment_force_complete: store.enrichment_force_complete,
    leadscrape_targeting: store.leadscrape_targeting,
    fit_gate: store.fit_gate,
    mail_assistant_knowledge: hasMail ? buildMailAssistantKnowledgePayload(store) : [],
  };
  const debugPayload = {
    ...payload,
    mail_providers: selectedMailProviders,
    outreach_agent_ids: store.outreach_agent_ids,
  };

  async function createCampaign() {
    if (!store.campaign_name.trim()) {
      setError('Le nom de la campagne est requis.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await apiClient.post<{ id: string; status: string; created_at: string }>(
        '/v1/campaigns',
        payload,
      );

      let postCreateMessage: string | null = null;

      try {
        await apiClient.put(
          '/v1/campaign-fit/config',
          buildFitConfigPayload(data.id, currentWorkspace?.id ?? '', useWizardStore.getState()),
        );
      } catch (err) {
        postCreateMessage = getErrorMessage(
          err,
          "La campagne a ete creee, mais la configuration d'eligibilite n'a pas pu etre enregistree.",
        );
      }

      const csvDraft = useWizardStore.getState().csv_import_draft;
      if (csvDraft && csvDraft.rows.length > 0) {
        try {
          await apiClient.post(`/v1/campaigns/${data.id}/enrichment-imports/csv`, {
            workspace_id: currentWorkspace?.id ?? '',
            file_name: csvDraft.fileName,
            rows: csvDraft.rows,
            enrichment_providers: useWizardStore
              .getState()
              .enrichment_providers.filter((provider) => provider !== 'csv'),
          });
        } catch (err) {
          const message = getErrorMessage(
            err,
            "La campagne a ete creee, mais l'import CSV initial a echoue.",
          );
          postCreateMessage = postCreateMessage
            ? `${postCreateMessage} ${message}`
            : message;
        }
      }

      if (postCreateMessage) {
        toast.error(postCreateMessage);
      }

      const outreachAgentIds = useWizardStore.getState().outreach_agent_ids;
      if (hasLinkedInOutreach && outreachAgentIds.length > 0) {
        try {
          await Promise.all(
            outreachAgentIds.map((agentId) =>
              apiClient.post(`/v1/outreach/agents/${agentId}/campaigns`, {
                workspace_id: currentWorkspace?.id ?? '',
                campaign_id: data.id,
              }),
            ),
          );
        } catch (err) {
          toast.error(
            getErrorMessage(
              err,
              "La campagne a ete creee, mais l'assignation des agents Outreach a echoue.",
            ),
          );
        }
      }

      navigate(ROUTES.CAMPAIGN_DETAIL(data.id));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Une erreur est survenue.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Recapitulatif"
        subtitle="Verifie tous les parametres avant de creer la campagne."
      />
      <CardContent className="space-y-5" data-testid="w8-summary-section">
        <div className="space-y-1">
          <Label htmlFor="campaign-name">Nom de la campagne *</Label>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <Input
              id="campaign-name"
              value={store.campaign_name}
              onChange={(event) => store.setCampaignName(event.target.value)}
              placeholder="Ex. : CH_FR-restaurants-q2-acme"
              data-testid="campaign-name-input"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => store.useGeneratedCampaignName(generatedCampaignName)}
              disabled={!generatedCampaignName}
              data-testid="use-generated-campaign-name"
            >
              Utiliser le format auto
            </Button>
          </div>
          <p className="text-xs text-gray-500" data-testid="campaign-name-pattern-help">
            Format synchronise: {generatedCampaignName || 'Completer pays, langue, industry, campaign id et brand.'}
          </p>
        </div>

        <hr className="border-brand-border" />

        <Section title="Channels">
          {store.channels_enabled
            .map((channel) => {
              if (channel === 'mail') return 'Email';
              if (channel === 'call') return 'Call';
              if (channel === 'formfill') return 'Form-fill';
              if (channel === 'linkedin_outreach') return 'LinkedIn Outreach';
              return channel;
            })
            .join(' + ') || '-'}
        </Section>

        <Section title="Sender">
          {store.sender_company_name || '-'} - {store.sender_email || '-'}
        </Section>

        <Section title="Offer">{store.offer_title || '-'}</Section>

        <Section title="Target">
          {store.target_country || '-'}
          {store.target_city ? ` - ${store.target_city}` : ''}
        </Section>

        <Section title="Eligibility & suppression">
          ICP : {store.fit_gate.ideal_customer_profile || '-'}
          <br />
          Blocked roles : {splitFreeformList(store.fit_gate.blocked_roles).length}
          <br />
          Suppressions : {fitConfigPayload.suppression_entries.length}
        </Section>

        {hasMail && (
          <>
            <Section title="Email templates">
              <ul className="space-y-0.5 list-none">
                <li data-testid="recap-email01">email01 : {summarize(store.email_templates.email01)}</li>
                <li data-testid="recap-email02">email02 : {summarize(store.email_templates.email02)}</li>
                <li data-testid="recap-email03">email03 : {summarize(store.email_templates.email03)}</li>
              </ul>
            </Section>

            <Section title="Providers & comptes">
              {selectedMailProviders.length > 0
                ? selectedMailProviders.join(' + ')
                : '(non specifie)'}{' '}
              - {store.mail_provider_email_account_ids.length} compte(s)
            </Section>

            <Section title="Reply-To">
              {store.reply_to_address || '-'} (handler : {store.reply_to_brand_handler})
            </Section>

            <Section title="AI knowledge">
              Policy : {store.mail_assistant_policy.auto_reply_enabled ? 'auto-reply on' : 'manual review'}
              <br />
              Knowledge docs : {store.mail_assistant_docs.filter((doc) => doc.title.trim() && doc.content_md.trim()).length}
              <br />
              Safe labels : {store.mail_assistant_policy.auto_reply_labels.join(', ') || '-'}
            </Section>
          </>
        )}

        {hasFormfill && (
          <Section title="Form-fill message">{summarize(store.form_message_template)}</Section>
        )}

        {hasCall && (
          <Section title="BIG.call">
            Workspace : {store.bigcall_workspace_id || '-'}
            <br />
            {store.bigcall_campaign_mode === 'create_new'
              ? `Nouvelle campagne : ${store.new_bigcall_campaign_name || '(auto-generee)'}`
              : `Campagne existante : ${store.bigcall_campaign_id ?? '-'}`}
          </Section>
        )}

        {hasLinkedInOutreach && (
          <Section title="LinkedIn Outreach">
            {store.outreach_agent_ids.length} agent(s) selectionne(s) seront assignes apres creation.
            <br />
            Quotas et connexion LinkedIn restent geres agent par agent depuis le cockpit Outreach.
          </Section>
        )}

        <Section title="LeadScrape targeting">
          {store.leadscrape_targeting.industries.length} industrie(s) - {store.leadscrape_targeting.locations.length} localisation(s)
        </Section>

        <Section title="Enrichment sources">
          {store.enrichment_providers.length > 0
            ? store.enrichment_providers.join(' + ')
            : '-'}
          <br />
          CSV initial : {store.csv_import_draft ? `${store.csv_import_draft.rows.length} ligne(s)` : 'aucun fichier charge'}
        </Section>

        <hr className="border-brand-border" />

        <details data-testid="json-payload-collapsible">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
            Voir le payload JSON brut (debug)
          </summary>
          <pre
            className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-64"
            data-testid="json-payload"
          >
            {JSON.stringify(debugPayload, null, 2)}
          </pre>
        </details>

        <hr className="border-brand-border" />

        <Button
          onClick={createCampaign}
          loading={isSubmitting}
          size="lg"
          className="w-full"
          data-testid="create-campaign-button"
          disabled={!store.campaign_name.trim()}
        >
          Creer la campagne
        </Button>

        {error && (
          <div
            role="alert"
            className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="submit-error"
          >
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
